import { componentQrl, inlinedQrl, _jsxBranch, useComputedQrl, useLexicalScope, useSignal, useStore, useOn, useOnDocument, useContextProvider, useTaskQrl, _jsxC, Slot, _IMMUTABLE, _fnSignal } from "@builder.io/qwik";
import { Fragment } from "@builder.io/qwik/jsx-runtime";
import builderContext from "../../../context/builder.context.qwik.mjs";
import { evaluate } from "../../../functions/evaluate/evaluate.qwik.mjs";
import { fastClone } from "../../../functions/fast-clone.qwik.mjs";
import { fetchOneEntry } from "../../../functions/get-content/index.qwik.mjs";
import { isBrowser } from "../../../functions/is-browser.qwik.mjs";
import { isEditing } from "../../../functions/is-editing.qwik.mjs";
import { isPreviewing } from "../../../functions/is-previewing.qwik.mjs";
import { logFetch } from "../../../functions/log-fetch.qwik.mjs";
import { createRegisterComponentMessage } from "../../../functions/register-component.qwik.mjs";
import { _track } from "../../../functions/track/index.qwik.mjs";
import { getInteractionPropertiesForEvent } from "../../../functions/track/interaction.qwik.mjs";
import { getDefaultCanTrack } from "../../../helpers/canTrack.qwik.mjs";
import { getCookieSync } from "../../../helpers/cookie.qwik.mjs";
import { createEditorListener } from "../../../helpers/subscribe-to-editor.qwik.mjs";
import { setupBrowserForEditing } from "../../../scripts/init-editing.qwik.mjs";
import { triggerAnimation } from "../../block/animator.qwik.mjs";
import { needsElementRefDivForEditing } from "./enable-editor.helpers.qwik.mjs";
import { getWrapperClassName } from "./styles.helpers.qwik.mjs";
const mergeNewRootState = function mergeNewRootState2(props, state, showContentProps, elementRef, newData) {
  var _a, _b;
  const combinedState = {
    ...props.builderContextSignal.rootState,
    ...newData
  };
  if (props.builderContextSignal.rootSetState)
    (_b = (_a = props.builderContextSignal).rootSetState) == null ? void 0 : _b.call(_a, combinedState);
  else
    props.builderContextSignal.rootState = combinedState;
};
const mergeNewContent = function mergeNewContent2(props, state, showContentProps, elementRef, newContent) {
  var _a, _b, _c, _d, _e;
  const newContentValue = {
    ...props.builderContextSignal.content,
    ...newContent,
    data: {
      ...(_a = props.builderContextSignal.content) == null ? void 0 : _a.data,
      ...newContent == null ? void 0 : newContent.data
    },
    meta: {
      ...(_b = props.builderContextSignal.content) == null ? void 0 : _b.meta,
      ...newContent == null ? void 0 : newContent.meta,
      breakpoints: ((_c = newContent == null ? void 0 : newContent.meta) == null ? void 0 : _c.breakpoints) || ((_e = (_d = props.builderContextSignal.content) == null ? void 0 : _d.meta) == null ? void 0 : _e.breakpoints)
    }
  };
  props.builderContextSignal.content = newContentValue;
};
const processMessage = function processMessage2(props, state, showContentProps, elementRef, event) {
  return createEditorListener({
    model: props.model,
    trustedHosts: props.trustedHosts,
    callbacks: {
      configureSdk: (messageContent) => {
        var _a;
        const { breakpoints, contentId } = messageContent;
        if (!contentId || contentId !== ((_a = props.builderContextSignal.content) == null ? void 0 : _a.id))
          return;
        if (breakpoints)
          mergeNewContent(props, state, showContentProps, elementRef, {
            meta: {
              breakpoints
            }
          });
      },
      animation: (animation) => {
        triggerAnimation(animation);
      },
      contentUpdate: (newContent) => {
        mergeNewContent(props, state, showContentProps, elementRef, newContent);
      },
      stateUpdate: (newState) => {
        mergeNewRootState(props, state, showContentProps, elementRef, newState);
      }
    }
  })(event);
};
const onClick = function onClick2(props, state, showContentProps, elementRef, event) {
  var _a, _b;
  if (props.builderContextSignal.content) {
    const variationId = (_a = props.builderContextSignal.content) == null ? void 0 : _a.testVariationId;
    const contentId = (_b = props.builderContextSignal.content) == null ? void 0 : _b.id;
    _track({
      apiHost: props.apiHost,
      type: "click",
      canTrack: getDefaultCanTrack(props.canTrack),
      contentId,
      apiKey: props.apiKey,
      variationId: variationId !== contentId ? variationId : void 0,
      ...getInteractionPropertiesForEvent(event),
      unique: !state.clicked
    });
  }
  if (!state.clicked)
    state.clicked = true;
};
const runHttpRequests = function runHttpRequests2(props, state, showContentProps, elementRef) {
  var _a, _b;
  const requests = ((_b = (_a = props.builderContextSignal.content) == null ? void 0 : _a.data) == null ? void 0 : _b.httpRequests) ?? {};
  Object.entries(requests).forEach(([key, httpRequest]) => {
    if (!httpRequest)
      return;
    const isCoreRequest = typeof httpRequest === "object" && httpRequest["@type"] === "@builder.io/core:Request";
    if (state.httpReqsPending[key])
      return;
    if (state.httpReqsData[key] && !isEditing())
      return;
    const url = isCoreRequest ? httpRequest.request.url : httpRequest;
    state.httpReqsPending[key] = true;
    const evaluatedUrl = url.replace(/{{([^}]+)}}/g, (_match, group) => String(evaluate({
      code: group,
      context: props.context || {},
      localState: void 0,
      rootState: props.builderContextSignal.rootState,
      rootSetState: props.builderContextSignal.rootSetState
    })));
    const fetchRequestObj = isCoreRequest ? {
      url: evaluatedUrl,
      method: httpRequest.request.method,
      headers: httpRequest.request.headers,
      body: httpRequest.request.body
    } : {
      url: evaluatedUrl,
      method: "GET"
    };
    logFetch(JSON.stringify(fetchRequestObj));
    fetch(fetchRequestObj.url, {
      method: fetchRequestObj.method,
      headers: fetchRequestObj.headers,
      body: fetchRequestObj.body
    }).then((response) => response.json()).then((json) => {
      mergeNewRootState(props, state, showContentProps, elementRef, {
        [key]: json
      });
      state.httpReqsData[key] = true;
    }).catch((err) => {
      console.error("error fetching dynamic data", JSON.stringify(httpRequest), err);
    }).finally(() => {
      state.httpReqsPending[key] = false;
    });
  });
};
const emitStateUpdate = function emitStateUpdate2(props, state, showContentProps, elementRef) {
  if (isEditing())
    window.dispatchEvent(new CustomEvent("builder:component:stateChange", {
      detail: {
        state: fastClone(props.builderContextSignal.rootState),
        ref: {
          name: props.model
        }
      }
    }));
};
const EnableEditor = /* @__PURE__ */ componentQrl(/* @__PURE__ */ inlinedQrl((props) => {
  var _a, _b;
  _jsxBranch();
  const showContentProps = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    const [props2] = useLexicalScope();
    return props2.showContent ? {} : {
      hidden: true,
      "aria-hidden": true
    };
  }, "EnableEditor_component_showContentProps_useComputed_aHr3V0Y8sNs", [
    props
  ]));
  const elementRef = useSignal();
  const state = useStore({
    ContentWrapper: props.contentWrapper || "div",
    clicked: false,
    hasExecuted: false,
    httpReqsData: {},
    httpReqsPending: {}
  }, {
    deep: true
  });
  useOn("initeditingbldr", /* @__PURE__ */ inlinedQrl((event, element) => {
    const [elementRef2, props2, showContentProps2, state2] = useLexicalScope();
    window.addEventListener("message", processMessage.bind(null, props2, state2, showContentProps2, elementRef2));
    setupBrowserForEditing({
      ...props2.locale ? {
        locale: props2.locale
      } : {},
      ...props2.enrich ? {
        enrich: props2.enrich
      } : {},
      ...props2.trustedHosts ? {
        trustedHosts: props2.trustedHosts
      } : {},
      modelName: props2.model ?? "",
      apiKey: props2.apiKey
    });
    Object.values(props2.builderContextSignal.componentInfos).forEach((registeredComponent) => {
      var _a2, _b2;
      if (!((_a2 = registeredComponent.models) == null ? void 0 : _a2.length) || registeredComponent.models.includes(props2.model)) {
        const message = createRegisterComponentMessage(registeredComponent);
        (_b2 = window.parent) == null ? void 0 : _b2.postMessage(message, "*");
      }
    });
    window.addEventListener("builder:component:stateChangeListenerActivated", emitStateUpdate.bind(null, props2, state2, showContentProps2, elementRef2));
  }, "EnableEditor_component_useOn_Qs8c0yql2i0", [
    elementRef,
    props,
    showContentProps,
    state
  ]));
  useOn("initpreviewingbldr", /* @__PURE__ */ inlinedQrl((event, element) => {
    var _a2;
    const [elementRef2, props2, showContentProps2, state2] = useLexicalScope();
    const searchParams = new URL(location.href).searchParams;
    const searchParamPreviewModel = searchParams.get("builder.preview");
    const searchParamPreviewId = searchParams.get(`builder.overrides.${searchParamPreviewModel}`);
    const previewApiKey = searchParams.get("apiKey") || searchParams.get("builder.space");
    if (searchParamPreviewModel === "BUILDER_STUDIO" || searchParamPreviewModel === props2.model && previewApiKey === props2.apiKey && (!props2.content || searchParamPreviewId === props2.content.id))
      fetchOneEntry({
        model: props2.model,
        apiKey: props2.apiKey,
        apiVersion: props2.builderContextSignal.apiVersion,
        ...searchParamPreviewModel === "BUILDER_STUDIO" && ((_a2 = props2.context) == null ? void 0 : _a2.symbolId) ? {
          query: {
            id: props2.context.symbolId
          }
        } : {}
      }).then((content) => {
        if (content)
          mergeNewContent(props2, state2, showContentProps2, elementRef2, content);
      });
  }, "EnableEditor_component_useOn_1_F94YipmknvI", [
    elementRef,
    props,
    showContentProps,
    state
  ]));
  useOnDocument("readystatechange", /* @__PURE__ */ inlinedQrl((event, element) => {
    var _a2, _b2, _c, _d, _e;
    const [props2, state2] = useLexicalScope();
    if (state2.hasExecuted)
      return;
    if (isBrowser()) {
      state2.hasExecuted = true;
      if (isEditing() && !props2.isNestedRender) {
        if (element)
          element.dispatchEvent(new CustomEvent("initeditingbldr"));
      }
      const shouldTrackImpression = ((_a2 = element.attributes.getNamedItem("shouldTrack")) == null ? void 0 : _a2.value) === "true";
      const winningVariantId = getCookieSync({
        name: `builder.tests.${(_b2 = props2.builderContextSignal.content) == null ? void 0 : _b2.id}`,
        canTrack: true
      });
      const variationId = (_c = element.attributes.getNamedItem("variationId")) == null ? void 0 : _c.value;
      if (shouldTrackImpression && variationId === winningVariantId) {
        const contentId = (_d = element.attributes.getNamedItem("contentId")) == null ? void 0 : _d.value;
        const apiKeyProp = (_e = element.attributes.getNamedItem("apiKey")) == null ? void 0 : _e.value;
        _track({
          apiHost: props2.apiHost,
          type: "impression",
          canTrack: true,
          contentId,
          apiKey: apiKeyProp,
          variationId: winningVariantId !== contentId ? winningVariantId : void 0
        });
      }
      if (isPreviewing() && !isEditing()) {
        if (element)
          element.dispatchEvent(new CustomEvent("initpreviewingbldr"));
      }
    }
  }, "EnableEditor_component_useOnDocument_H7syEGJXM4Y", [
    props,
    state
  ]));
  useContextProvider(builderContext, props.builderContextSignal);
  useTaskQrl(/* @__PURE__ */ inlinedQrl(() => {
    const [elementRef2, props2, showContentProps2, state2] = useLexicalScope();
    runHttpRequests(props2, state2, showContentProps2, elementRef2);
    emitStateUpdate(props2);
  }, "EnableEditor_component_useTask_Nb2VI04qp0M", [
    elementRef,
    props,
    showContentProps,
    state
  ]));
  useTaskQrl(/* @__PURE__ */ inlinedQrl(({ track }) => {
    const [elementRef2, props2, showContentProps2, state2] = useLexicalScope();
    track(() => props2.content);
    if (props2.content)
      mergeNewContent(props2, state2, showContentProps2, elementRef2, props2.content);
  }, "EnableEditor_component_useTask_1_m0y1Z9vk4eQ", [
    elementRef,
    props,
    showContentProps,
    state
  ]));
  useTaskQrl(/* @__PURE__ */ inlinedQrl(({ track }) => {
    const [elementRef2, props2, showContentProps2, state2] = useLexicalScope();
    track(() => props2.builderContextSignal.rootState);
    emitStateUpdate(props2);
  }, "EnableEditor_component_useTask_2_xVyv0tDqZLs", [
    elementRef,
    props,
    showContentProps,
    state
  ]));
  useTaskQrl(/* @__PURE__ */ inlinedQrl(({ track }) => {
    const [elementRef2, props2, showContentProps2, state2] = useLexicalScope();
    track(() => props2.data);
    if (props2.data)
      mergeNewRootState(props2, state2, showContentProps2, elementRef2, props2.data);
  }, "EnableEditor_component_useTask_3_bQ0e5LHZwWE", [
    elementRef,
    props,
    showContentProps,
    state
  ]));
  useTaskQrl(/* @__PURE__ */ inlinedQrl(({ track }) => {
    const [elementRef2, props2, showContentProps2, state2] = useLexicalScope();
    track(() => props2.locale);
    if (props2.locale)
      mergeNewRootState(props2, state2, showContentProps2, elementRef2, {
        locale: props2.locale
      });
  }, "EnableEditor_component_useTask_4_moHYZG8uNVU", [
    elementRef,
    props,
    showContentProps,
    state
  ]));
  return /* @__PURE__ */ _jsxC(Fragment, {
    children: props.builderContextSignal.content || needsElementRefDivForEditing() ? /* @__PURE__ */ _jsxC(state.ContentWrapper, {
      get apiKey() {
        return props.apiKey;
      },
      get contentId() {
        var _a2;
        return (_a2 = props.builderContextSignal.content) == null ? void 0 : _a2.id;
      },
      get variationId() {
        var _a2;
        return (_a2 = props.builderContextSignal.content) == null ? void 0 : _a2.testVariationId;
      },
      onClick$: /* @__PURE__ */ inlinedQrl((event) => {
        const [elementRef2, props2, showContentProps2, state2] = useLexicalScope();
        return onClick(props2, state2, showContentProps2, elementRef2, event);
      }, "EnableEditor_component__Fragment_onClick_b4nYT3DrjEk", [
        elementRef,
        props,
        showContentProps,
        state
      ]),
      ref: elementRef,
      shouldTrack: String(props.builderContextSignal.content && getDefaultCanTrack(props.canTrack)),
      get "builder-content-id"() {
        var _a2;
        return (_a2 = props.builderContextSignal.content) == null ? void 0 : _a2.id;
      },
      get "builder-model"() {
        return props.model;
      },
      className: getWrapperClassName(((_a = props.content) == null ? void 0 : _a.testVariationId) || ((_b = props.content) == null ? void 0 : _b.id)),
      style: {
        display: !props.builderContextSignal.content && needsElementRefDivForEditing() ? "none" : void 0
      },
      ...showContentProps.value,
      ...props.contentWrapperProps,
      children: /* @__PURE__ */ _jsxC(Slot, null, 3, "06_0"),
      [_IMMUTABLE]: {
        apiKey: _fnSignal((p0) => p0.apiKey, [
          props
        ], "p0.apiKey"),
        "builder-content-id": _fnSignal((p0) => {
          var _a2;
          return (_a2 = p0.builderContextSignal.content) == null ? void 0 : _a2.id;
        }, [
          props
        ], "p0.builderContextSignal.content?.id"),
        "builder-model": _fnSignal((p0) => p0.model, [
          props
        ], "p0.model"),
        contentId: _fnSignal((p0) => {
          var _a2;
          return (_a2 = p0.builderContextSignal.content) == null ? void 0 : _a2.id;
        }, [
          props
        ], "p0.builderContextSignal.content?.id"),
        ref: _IMMUTABLE,
        variationId: _fnSignal((p0) => {
          var _a2;
          return (_a2 = p0.builderContextSignal.content) == null ? void 0 : _a2.testVariationId;
        }, [
          props
        ], "p0.builderContextSignal.content?.testVariationId")
      }
    }, 0, "06_1") : null
  }, 1, "06_2");
}, "EnableEditor_component_ko1mO8oaj8k"));
export {
  EnableEditor,
  EnableEditor as default,
  emitStateUpdate,
  mergeNewContent,
  mergeNewRootState,
  onClick,
  processMessage,
  runHttpRequests
};
